/**
 * Rex Mortis — Terms re-acceptance gate.
 *
 * Mounted unconditionally on every authenticated page render. Checks
 * the cached account in the store: if `termsCurrent === false`, opens a
 * blocking modal over the page. The modal disables all interaction
 * with the underlying app until the player ticks "I accept" and the
 * server records the new acceptance.
 *
 * Triggered by:
 *   - Server bumping `TERMS_VERSION` in server/src/config/terms.ts. The
 *     bumped version flows through `/me` → store → this gate.
 *   - Any login by a player whose stored version is older than current.
 */

import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';

const MODAL_ID = 'rm-terms-modal';

export function mountTermsAcceptanceGate(): void {
  const account = store.get().account;
  if (!account) return;
  // emailVerified gate is separate; terms gate only fires when the
  // server tells us termsCurrent is explicitly false.
  if (account.termsCurrent !== false) {
    const existing = document.getElementById(MODAL_ID);
    if (existing) existing.remove();
    return;
  }

  // Don't double-mount.
  if (document.getElementById(MODAL_ID)) return;

  const overlay = document.createElement('div');
  overlay.id = MODAL_ID;
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'rm-terms-title');
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.72);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  `;

  overlay.innerHTML = `
    <div style="
      max-width: 560px;
      width: 100%;
      max-height: 90vh;
      overflow-y: auto;
      background: var(--c-parchment);
      background-image: var(--parchment-texture);
      border: 1px solid var(--c-rule-strong);
      border-radius: var(--radius);
      box-shadow: var(--shadow-lift), var(--inner-frame);
      padding: var(--s-5) var(--s-6);
    ">
      <h2 id="rm-terms-title" style="margin-top: 0;">A New Edict from the Realm</h2>
      <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -4px;">
        The Rules of the Realm or the Terms of Service have been updated since you last agreed.
        Please read the new versions and accept to continue.
      </p>

      <div style="margin: var(--s-4) 0; display: flex; gap: var(--s-3); flex-wrap: wrap;">
        <a href="#/rules" target="_blank" rel="noopener" class="btn ghost">Read the Rules</a>
        <a href="#/tos" target="_blank" rel="noopener" class="btn ghost">Read the Terms of Service</a>
        <a href="#/privacy" target="_blank" rel="noopener" class="btn ghost">Read the Privacy Policy</a>
      </div>

      <label style="display: flex; gap: 10px; align-items: flex-start; cursor: pointer; line-height: 1.4; margin: var(--s-4) 0;">
        <input id="rm-terms-checkbox" type="checkbox"
               style="margin-top: 4px; width: 18px; height: 18px; cursor: pointer; flex-shrink: 0;" />
        <span>I have read and agree to the updated <strong>Rules of the Realm</strong>, <strong>Terms of Service</strong>, and <strong>Privacy Policy</strong>.</span>
      </label>

      <div id="rm-terms-error" class="alert danger" hidden style="margin-bottom: var(--s-3);"></div>

      <div style="display: flex; gap: var(--s-2); justify-content: flex-end;">
        <button class="btn ghost" id="rm-terms-signout">Sign out instead</button>
        <button class="btn btn-primary" id="rm-terms-accept" disabled>Accept and continue</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  const checkbox = overlay.querySelector<HTMLInputElement>('#rm-terms-checkbox')!;
  const acceptBtn = overlay.querySelector<HTMLButtonElement>('#rm-terms-accept')!;
  const signoutBtn = overlay.querySelector<HTMLButtonElement>('#rm-terms-signout')!;
  const errBox = overlay.querySelector<HTMLDivElement>('#rm-terms-error')!;

  checkbox.addEventListener('change', () => {
    acceptBtn.disabled = !checkbox.checked;
  });

  acceptBtn.addEventListener('click', async () => {
    if (!checkbox.checked) return;
    acceptBtn.disabled = true;
    acceptBtn.textContent = 'Accepting…';
    errBox.hidden = true;
    try {
      const r = await api.acceptTerms();
      // Patch the store so future page renders don't re-trigger this modal.
      const s = store.get();
      if (s.account) {
        store.set({
          ...s,
          account: { ...s.account, termsCurrent: r.termsCurrent, termsVersion: r.termsVersion },
        });
      }
      overlay.remove();
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Could not save your acceptance.';
      errBox.textContent = msg;
      errBox.hidden = false;
      acceptBtn.disabled = false;
      acceptBtn.textContent = 'Accept and continue';
    }
  });

  signoutBtn.addEventListener('click', async () => {
    try { await api.logout(); } catch { /* ignore */ }
    store.clear();
    window.location.hash = '#/login';
    overlay.remove();
  });
}
